// src/components/Colaboradores/index.tsx
import { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, UserCircleIcon, FunnelIcon, KeyIcon, UsersIcon } from '@heroicons/react/24/outline';
import type { Colaborador, Departamento, User, UserRole } from '../../types';
import { checkUserManagementPermissions } from '../../types';
import ColaboradorModal from './ColaboradorModal';

interface ColaboradoresProps {
  initialColaboradores: Colaborador[];
  departamentos: Departamento[];
  users: User[];
  flows?: Array<{ id: string; nome?: string; name?: string }>;
  empresaId: string;
  onColaboradoresChange: (colaboradores: Colaborador[]) => void;
  onUserChange: (userId: string, userData: { 
    senha?: string;
    role?: UserRole;
    status?: 'ativo' | 'inativo';
    permissoes?: { menus?: any[] };
  }) => void;
  onUserCreate: (userData: {
    id: string;
    nome: string;
    email: string;
    senha: string;
    role: UserRole;
    departamentoId?: string;
    status: 'ativo' | 'inativo';
    empresaId: string;
  }) => void;
  userPermissions: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: User;
  };
  // Relê os acessos (users) do servidor. O backend cria o acesso junto com o
  // colaborador, mas a lista de users do App não sabia disso: a coluna Tipo
  // ficava "Sem acesso" até o F5.
  onUsersReload?: () => void;
}

const Colaboradores = ({
  initialColaboradores,
  departamentos,
  users,
  flows = [],
  empresaId,
  onColaboradoresChange,
  onUserChange,
  onUserCreate,
  userPermissions,
  onUsersReload
}: ColaboradoresProps) => {
  // Garantir que sempre temos um array válido
  const initialColabsArray = Array.isArray(initialColaboradores) ? initialColaboradores : [];
  
  const [colaboradores, setColaboradores] = useState<Colaborador[]>(initialColabsArray);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingColaborador, setEditingColaborador] = useState<Colaborador | null>(null);
  const [selectedDepartamento, setSelectedDepartamento] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    // Log apenas quando necessário - não a cada render
    if (initialColaboradores?.length !== colaboradores?.length) {
      console.log('🔄 useEffect Colaboradores - atualizando com:', initialColaboradores?.length || 0, 'colaboradores');
    }
    
    // Garantir que sempre temos um array válido
    const newColabsArray = Array.isArray(initialColaboradores) ? initialColaboradores : [];
    setColaboradores(newColabsArray);
  }, [initialColaboradores]);

  const handleSaveColaborador = async (newColaboradorData: Omit<Colaborador, 'id' | 'createdAt' | 'updatedAt'> | Colaborador) => {
    try {
      if ('id' in newColaboradorData) {
        // Editar colaborador existente - AGORA COM CHAMADA PARA O BACKEND
        console.log('🔄 Atualizando colaborador no backend:', newColaboradorData);
        
        const colaboradorAtualizado = {
          ...newColaboradorData,
          updatedAt: new Date(),
          empresaId: empresaId
        };

        // Remover campos que não devem ser atualizados no MongoDB
        const { _id, id, ...dadosParaAtualizar } = colaboradorAtualizado as any;

        const colaboradorId = (newColaboradorData as any)._id || newColaboradorData.id;
        const response = await fetch(`/api/colaboradores/${colaboradorId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': userPermissions.currentUser?.email || ''
          },
          body: JSON.stringify(dadosParaAtualizar)
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 403) {
            throw new Error(`Acesso negado: ${errorData.error || 'Você não tem permissão para editar colaboradores'}`);
          }
          throw new Error(errorData.error || 'Erro ao atualizar colaborador no servidor');
        }

        const result = await response.json();
        console.log('✅ Colaborador atualizado no backend:', result);

        // Recarregar colaboradores do backend para sincronizar
        await reloadColaboradores();
        onUsersReload?.();   // o PUT também propaga nome/e-mail/role para users
        showNotification('success', 'Colaborador atualizado com sucesso!');
      } else {
        // Adicionar novo colaborador - AGORA COM CHAMADA PARA O BACKEND
        console.log('🚀 Criando novo colaborador no backend:', newColaboradorData);
        
        const response = await fetch('/api/colaboradores', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': userPermissions.currentUser?.email || ''
          },
          body: JSON.stringify({
            ...newColaboradorData,
            empresaId: empresaId // Garantir que a empresa seja enviada
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erro ao criar colaborador no servidor');
        }

        const result = await response.json();
        console.log('✅ Colaborador criado no backend:', result);

        // Atualizar o estado local com o colaborador retornado do backend
        const colaboradorData = result.data || result.colaborador || result;
        const novoColaborador: Colaborador = {
          id: result.colaborador_id || colaboradorData._id || colaboradorData.id,
          ...colaboradorData,
          createdAt: new Date(colaboradorData.createdAt),
          updatedAt: new Date(colaboradorData.updatedAt)
        };

        const updatedColaboradores = [...colaboradores, novoColaborador];
        setColaboradores(updatedColaboradores);
        onColaboradoresChange(updatedColaboradores);
        if (result.user_created) onUsersReload?.();
        showNotification('success', `Colaborador ${result.data.nome} criado com sucesso! ${result.user_created ? 'Acesso ao sistema também configurado.' : ''}`);
      }
      setModalOpen(false);
      setEditingColaborador(null);
    } catch (error) {
      console.error('❌ Erro ao salvar colaborador:', error);
      showNotification('error', `Erro ao salvar colaborador: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  const handleSaveAuth = (userId: string, userData: { 
    senha?: string;
    role: UserRole;
    status: 'ativo' | 'inativo';
    permissoes?: { menus?: any[] };
  }) => {
    // Verifica se o usuário já existe
    const existingUser = users.find(user => user.id === userId);
    
    if (existingUser) {
      // Atualiza usuário existente
      onUserChange(userId, userData);
      showNotification('success', 'Dados de acesso atualizados com sucesso!');
    } else {
      // Cria novo usuário
      const colaborador = colaboradores.find(col => col.email === userId || col.id === userId);
      if (!colaborador) {
        showNotification('error', 'Erro ao criar usuário: colaborador não encontrado');
        return;
      }

      onUserCreate({
        id: userId,
        nome: colaborador.nome,
        email: colaborador.email,
        senha: userData.senha,
        role: userData.role,
        departamentoId: colaborador.departamentoId,
        status: userData.status,
        empresaId: empresaId
      });
      showNotification('success', 'Conta de acesso criada com sucesso!');
    }
  };

  const handleDeleteColaborador = (id: string) => {
    const isResponsavel = departamentos.some(dep => dep.responsavel === id);
    const isSuperior = colaboradores.some(col => col.superiorId === id);
    
    if (isResponsavel) {
      showNotification('error', 'Este colaborador é responsável por um departamento. Remova-o da função de responsável antes de excluí-lo.');
      return;
    }

    if (isSuperior) {
      showNotification('error', 'Este colaborador é superior de outros colaboradores. Atualize a hierarquia antes de excluí-lo.');
      return;
    }

    if (window.confirm('Tem certeza que deseja remover este colaborador?')) {
      const updatedColaboradores = colaboradores.filter(col => col.id !== id);
      setColaboradores(updatedColaboradores);
      onColaboradoresChange(updatedColaboradores);
      showNotification('success', 'Colaborador removido com sucesso!');
    }
  };

  const getDepartamentoNome = (departamentoId: string) => {
    return departamentos.find(dep => dep.id === departamentoId)?.nome || 'Departamento não encontrado';
  };

  const getSuperiorNome = (superiorId: string | undefined) => {
    if (!superiorId) return 'Não definido';
    const superior = colaboradores.find(col => col.id === superiorId);
    return superior ? `${superior.nome} (${superior.cargo})` : 'Não encontrado';
  };

  const getUserAccount = (email: string) => {
    const user = users.find(u => u.email === email);
    if (!user) return null;
    return user;
  };

  const getRoleName = (role: UserRole) => {
    switch (role) {
      case 'administrator':
      case 'admin':
      case 'company_admin':
      case 'global_admin':
        return 'Administrador';
      case 'editor':
        return 'Editor';
      case 'viewer':
        return 'Consulta';
      default:
        return role;
    }
  };

  const filteredColaboradores = colaboradores.filter(col => {
    const matchDep = !selectedDepartamento || col.departamentoId === selectedDepartamento;
    if (!matchDep) return false;
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const depNome = getDepartamentoNome(col.departamentoId).toLowerCase();
    const supNome = getSuperiorNome(col.superiorId).toLowerCase();
    return (
      col.nome.toLowerCase().includes(term) ||
      col.email.toLowerCase().includes(term) ||
      (col.cargo || '').toLowerCase().includes(term) ||
      (col.telefone || '').toLowerCase().includes(term) ||
      depNome.includes(term) ||
      supNome.includes(term)
    );
  });

  const reloadColaboradores = async () => {
    try {
      const response = await fetch('/api/colaboradores', {
        method: 'GET',
        headers: {
          'X-User-Email': userPermissions.currentUser?.email || ''
        }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Mapear _id para id para compatibilidade com o frontend
          const colaboradoresAtualizados = result.data.map((col: any) => ({
            ...col,
            id: col._id || col.id,
            createdAt: new Date(col.createdAt),
            updatedAt: new Date(col.updatedAt)
          }));
          setColaboradores(colaboradoresAtualizados);
          onColaboradoresChange(colaboradoresAtualizados);
          console.log('✅ Colaboradores recarregados do backend:', colaboradoresAtualizados.length);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao recarregar colaboradores:', error);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 max-w-[1400px] mx-auto">
      {/* Notification */}
      {notification && (
        <div 
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
            notification.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-sm font-semibold text-gray-900">Colaboradores</h1>
          <p className="text-xs text-gray-500">Gerencie os colaboradores e contas de acesso</p>
        </div>
        {checkUserManagementPermissions(userPermissions.currentUser?.role || 'viewer', 'create') && (
          <button
            onClick={() => setModalOpen(true)}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Novo Colaborador
          </button>
        )}
      </div>

      {colaboradores.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <FunnelIcon className="h-3.5 w-3.5 text-gray-400" />
              <select
                value={selectedDepartamento}
                onChange={(e) => setSelectedDepartamento(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="">Todos os departamentos</option>
                {departamentos.map((dep) => (
                  <option key={dep.id} value={dep.id}>{dep.nome}</option>
                ))}
              </select>
            </div>
            <div className="relative flex-1 max-w-xs">
              <svg className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome, cargo, e-mail..."
                className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {colaboradores.length === 0 ? (
        <div className="text-center py-8 bg-white/80 backdrop-blur-md rounded-lg border border-gray-200/50">
          <UserCircleIcon className="h-8 w-8 mx-auto text-gray-300" />
          <p className="mt-2 text-xs text-gray-500">Nenhum colaborador cadastrado</p>
          <button
            onClick={() => setModalOpen(true)}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-md text-xs font-medium hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Adicionar Colaborador
          </button>
        </div>
      ) : (
        <div className="bg-white/80 backdrop-blur-md rounded-lg border border-gray-200/50">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-gray-200 min-w-[920px] text-[0.7rem]">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-2 py-1.5 text-left text-[0.65rem] font-medium text-gray-500 uppercase tracking-wider w-[25%]">
                    Colaborador
                  </th>
                  <th scope="col" className="px-2 py-1.5 text-left text-[0.65rem] font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                    Telefone
                  </th>
                  <th scope="col" className="px-2 py-1.5 text-left text-[0.65rem] font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                    Cargo
                  </th>
                  <th scope="col" className="px-2 py-1.5 text-left text-[0.65rem] font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                    Departamento
                  </th>
                  <th scope="col" className="px-2 py-1.5 text-left text-[0.65rem] font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                    Superior
                  </th>
                  <th scope="col" className="px-2 py-1.5 text-left text-[0.65rem] font-medium text-gray-500 uppercase tracking-wider w-[8%] bg-gray-50">
                    Status
                  </th>
                  <th scope="col" className="px-2 py-1.5 text-left text-[0.65rem] font-medium text-gray-500 uppercase tracking-wider w-[10%] bg-gray-50">
                    Tipo
                  </th>
                  <th scope="col" className="relative px-2 py-1.5 w-[5%] bg-gray-50">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredColaboradores.map((colaborador) => {
                  const userAccount = getUserAccount(colaborador.email);
                  return (
                    <tr key={colaborador.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2 w-[25%]">
                        <div className="flex items-center">
                          {/* Miniatura da foto */}
                          <div className="flex-shrink-0 h-7 w-7 mr-2">
                            {(colaborador as any).foto ? (
                              <img
                                src={(colaborador as any).foto}
                                alt={`Foto de ${colaborador.nome}`}
                                className="h-7 w-7 rounded-full object-cover border border-gray-200"
                              />
                            ) : (
                              <div className="h-7 w-7 rounded-full bg-gray-300 flex items-center justify-center border border-gray-200">
                                <span className="text-[0.6rem] font-medium text-gray-600">
                                  {colaborador.nome.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="overflow-hidden">
                            <div className="text-[0.7rem] font-medium text-gray-900 truncate">{colaborador.nome}</div>
                            <div className="text-[0.65rem] text-gray-500 truncate">{colaborador.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-[0.7rem] text-gray-900 w-[12%] truncate">
                        {(colaborador as any).telefone || '-'}
                      </td>
                      <td className="px-2 py-2 text-[0.7rem] text-gray-900 w-[15%] truncate">
                        {colaborador.cargo}
                      </td>
                      <td className="px-2 py-2 text-[0.7rem] text-gray-900 w-[12%] truncate">
                        {getDepartamentoNome(colaborador.departamentoId)}
                      </td>
                      <td className="px-2 py-2 text-[0.7rem] text-gray-900 w-[15%] truncate">
                        {getSuperiorNome(colaborador.superiorId)}
                      </td>
                      <td className="px-2 py-2 w-[8%]">
                        <span className={`px-1.5 py-0.5 inline-flex text-[0.65rem] leading-tight font-semibold rounded-full ${
                          colaborador.status === 'ativo'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {colaborador.status === 'ativo' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-2 py-2 w-[10%]">
                        {userAccount ? (
                          <div className="flex items-center gap-0.5">
                            <KeyIcon className="h-3 w-3 text-blue-500" />
                            <span className={`text-[0.65rem] font-semibold truncate ${
                              ['administrator','admin','company_admin','global_admin'].includes(userAccount.role) ? 'text-purple-600' :
                              userAccount.role === 'editor' ? 'text-blue-600' :
                              'text-gray-600'
                            }`}>
                              {['administrator','admin','company_admin','global_admin'].includes(userAccount.role) ? 'Admin' :
                               userAccount.role === 'editor' ? 'Editor' :
                               'Consulta'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[0.65rem] text-gray-500">Sem acesso</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-sm font-medium w-[5%]">
                        <div className="flex justify-end gap-0.5">
                          {(userPermissions.canAdmin || 
                            (userPermissions.canEdit && userPermissions.currentUser?.departamentoId === colaborador.departamentoId)) && (
                            <button
                              onClick={() => {
                                setEditingColaborador(colaborador);
                                setModalOpen(true);
                              }}
                              className="p-0.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                              title="Editar colaborador"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {checkUserManagementPermissions(userPermissions.currentUser?.role || 'viewer', 'delete') && (
                            <button
                              onClick={() => handleDeleteColaborador(colaborador.id)}
                              className="p-0.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                              title="Excluir colaborador"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}      {modalOpen && (checkUserManagementPermissions(userPermissions.currentUser?.role || 'viewer', 'create') || 
                      checkUserManagementPermissions(userPermissions.currentUser?.role || 'viewer', 'edit')) && (
        <ColaboradorModal
          colaborador={editingColaborador}
          departamentos={departamentos}
          colaboradores={colaboradores}
          flows={flows}
          existingUsers={users}
          onSave={handleSaveColaborador}
          onSaveAuth={handleSaveAuth}
          onClose={() => {
            setModalOpen(false);
            setEditingColaborador(null);
          }}
          empresaId={empresaId}
          userPermissions={userPermissions}
        />
      )}
      </div>
    </div>
  );
};

export default Colaboradores;